import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom';
import arrowIcon from "../../../../assests/img/right-arrow-icon.svg";
import backIcon from "../../../../assests/img/back-icon.svg"
import overflowIcon from "../../../../assests/img/overflow-icon.svg"
import { CATEGORY_LIST, PRODUCT_DELTA_CATEGORY_LIST } from '../ProductConstants';
import BootBoxManager from 'Common/BootBoxManager';
import { AppContext } from 'Context/AppContext';
import { toast } from 'react-toastify';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';
import { ProductsContext } from '../ProductsContext';

interface ProductViewHeaderProps {
  productName: string;
  productid: string
  categoryid: string
}

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

const ProductViewHeader: FC<ProductViewHeaderProps> = ({ 
  productName, 
  productid, 
  categoryid 
}) => {
  const AppData = useContext(AppContext)
  const channelKey = AppData?.channelKey;
  const prodContext = useContext(ProductsContext);
  const history = useHistory();
  const updatePermission = AppData.pm.checkPermission("gmsproducts_update");
  const deletePermission = AppData.pm.checkPermission("gmsproducts_delete");

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleScroll = () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isMenuOpen]);

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom, left: rect.left });
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const handle_delete_Product: Function = async () => {
    if (!deletePermission) {
      toast.info(
        <div>
          You don&apos;t have permission to delete the product.
          <br />
          <strong>Reason:</strong> Insufficient privileges
        </div>
      );
      return;
    }
    let res = await ServiceWrapper.doGet(
      api_base_url + "product/delete/channel/" + channelKey + "/product/" + productid,
      null,
      null,
      AuthManager.getGmsToken()
    );
  
    if (!res.errorfound) {
      AppData.setProductDeleted(res.data.done);
      if (res.data.done) {
        AppData.reset();
        
        // Success cases with detailed messages
        if (res.data.completely_deleted) {
          toast.success(`Product completely deleted from all environments`);
          setTimeout(() => {
            history.goBack();
          }, 2000);
        } else if (res.data.remaining_channels && res.data.remaining_channels.length > 0) {
          toast.success(`Product removed from environment successfully`);
          toast.info(`Product still available in: ${res.data.remaining_channels.join(', ')}`);
          setTimeout(() => {
            history.goBack();
          }, 2000);
        } else {
          toast.success("Product completely deleted from all environments");
          setTimeout(() => {
            history.goBack();
          }, 2000);
        }
      } else {
        // Failure cases with specific error handling
        const productName = res.data.name || "Product";
        
        if (res.data.message.includes("does not exist")) {
          toast.error(
            <div>
              Unable to delete product.
              <br />
              <strong>Reason:</strong> {productName} does not exist
            </div>
          );
        } else if (res.data.message.includes("not mapped to channel")) {
          toast.warning(
            <div>
              Unable to delete product.
              <br />
              <strong>Reason:</strong> Product is not mapped to this environment
            </div>
          );
        } else if (res.data.message.includes("technical error") || res.data.message.includes("Neo4j Error")) {
          toast.error(
            <div>
              Unable to delete product.
              <br />
              <strong>Reason:</strong> Technical error occurred during deletion
            </div>
          );
        } else {
          toast.error(
            <div>
              Unable to delete product.
              <br />
              <strong>Reason:</strong> {res.data.message || "Failed to delete product"}
            </div>
          );
        }
      }
    } else {
      toast.error(
        <div>
          Unable to delete product.
          <br />
          <strong>Reason:</strong> Network error occurred while deleting product
        </div>
      );
    }
  };

  const categoryName = prodContext.categoryData?.map((categoryType) => categoryType.category).flat().find(category => category.id === categoryid)?.name

  return (
    <React.Fragment>
      <nav className={`tw-flex`} aria-label="Breadcrumb">
        <ol className={`tw-inline-flex tw-items-center tw-space-x-1 md:tw-space-x-3`}>
          <li className={`tw-inline-flex tw-items-center`}>
            <Link to={PRODUCT_DELTA_CATEGORY_LIST} className={`tw-text-xs tw-leading-[19.6px] hover:tw-text-gray-200 tw-font-medium tw-text-gray-200 md:tw-ml-2`}>
              {CATEGORY_LIST}
            </Link>
          </li>
          <li>
            <div className={`tw-flex tw-items-center tw-pointer-events-none`}>
              <img alt="" src={arrowIcon} />
              <Link to="#" className={`tw-ml-1 tw-text-xs tw-leading-[19.6px] tw-font-medium tw-text-gray-200 md:tw-ml-2`}>{prodContext.findSelectedCategoryTypeName(prodContext.categoryData, categoryid) ?? ""}</Link>
            </div>
          </li>
          <li>
            <div className={`tw-flex tw-items-center`}>
              <img alt="" src={arrowIcon} />
              <Link to={`/gms/products/list/${categoryid}`} className={`tw-ml-1 tw-text-xs tw-leading-[19.6px] tw-font-medium hover:tw-text-gray-200 tw-text-gray-200 md:tw-ml-2`}>{categoryName ?? ""}</Link>
            </div>
          </li>
          <li>
            <div className={`tw-flex tw-items-center tw-pointer-events-none`}>
              <img alt="" src={arrowIcon} />
              <Link to="#" className={`tw-ml-1 tw-text-xs tw-leading-[19.6px] tw-font-medium tw-text-gray-200 md:tw-ml-2`}>{productName}</Link>
            </div>
          </li>
        </ol>
      </nav>
      <div className={`tw-flex tw-justify-between tw-items-center`}>
        <div className={`tw-mt-3 tw-flex tw-items-center`}>
          <Link to={`/gms/products/list/${categoryid}`}>
            <img className={`tw-cursor-pointer`} src={backIcon} alt="Back Icon" />
          </Link>
          <div className={`tw-flex tw-flex-row tw-py-0 tw-px-2.5 tw-items-center tw-justify-center tw-gap-[10px] tw-text-5xl tw-text-gray-300`}>
            <div className={`tw-leading-[25.2px] tw-text-[20px] tw-text-[#091E42] tw-font-semibold`}>{productName}</div>
            {/* <div className={`tw-rounded-[44px] tw-bg-[#DFE1E6] tw-h-[26px] tw-flex tw-flex-row tw-py-0.5 tw-px-3 tw-box-border tw-items-center tw-justify-center tw-text-slategray-200`}>
                    <div className={`tw-text-sm tw-font-normal tw-text-[#5C5F62] tw-bg-[#E2E2E2`}>Draft</div>
                </div> */}
          </div>
        </div>
        <div className={`tw-flex tw-gap-2`}>
          {/* <DeltaSwitch labels={['Live', 'Draft', 'Rollback']}/> */}
          <button onClick={() => { if (updatePermission) { history.push(`/gms/product/edit/${productid}`) } else { toast.info("You don't have permission to Update the Products."); } }} className="button-style">{"Edit Product"}</button>
          {/* <button onClick={() => history.push("/gms/product/details/9ff30e8a3b7211edb14016ea03dc3d63")} className={`button-style`}>{"Publish to live"}</button> */}
          <div className="tw-relative">
            <button
              ref={toggleRef}
              className="tw-flex tw-items-center tw-rounded-md tw-p-2 hover:tw-bg-gray-100 focus:tw-outline-none"
              onClick={toggleMenu}
            >
              <img src={overflowIcon} alt="" />
            </button>

            {isMenuOpen && (
              <div
                ref={menuRef}
                className="tw-fixed tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0] tw-shadow-lg"
                style={{ top: `${menuPosition.top + 10}px`, left: `${menuPosition.left - 50}px` }}
              >
                <div className="tw-py-1">
                  <button
                    onClick={() => {
                      if (deletePermission) {
                        BootBoxManager.confirm(
                          "",
                          `${productName}`,
                          false,
                          [handle_delete_Product, null]
                        );
                      } else {
                        toast.info(
                          "You don't have permission to delete this product"
                        );
                      }
                      setIsMenuOpen(false);
                    }}
                    className="tw-flex tw-items-center tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={`tw-box-border tw-w-auto tw-h-px tw-border-t-[1px] tw-border-solid tw-border-[#D1D1D6] tw-mt-2`} />
    </React.Fragment>
  )
}

export default ProductViewHeader;